import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronDown
} from 'lucide-react';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubdropdown, setActiveSubdropdown] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!isHome) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHome]);

  const navItems = [
    {
      name: 'About Us',
      href: '/about',
      dropdown: [
        { name: 'TAG\'s Mission Statement', href: '/about/mission' },
        { name: 'TAG Virtual Tour', href: '/about/virtual-tour' },
        { name: 'Staff List', href: '/about/staff' },
      ]
    },
    {
      name: 'Admissions',
      href: '/admissions'
    },
    {
      name: 'Academics',
      href: '/academics',
      dropdown: [
        { name: 'Curriculum Conference', href: '/academics/curriculum-conference' },
        { name: '8th Grade Requirements', href: '/academics/8th-grade' },
        { name: 'SHSAT Preparation', href: '/academics/shsat' },
        { name: 'High School Applications', href: '/academics/high-school' },
        {
          name: 'Learning Resources',
          href: '/academics/learning-resources',
          subdropdown: [
            { name: 'Smarty Ants - ELA Resource', href: 'https://play.smartyants.com/login', external: true },
            { name: 'SplashLearn - Math Resource', href: 'https://www.splashlearn.com/', external: true },
            { name: 'Get Epic - ELA Resource', href: 'https://www.getepic.com/', external: true },
            { name: 'Zearn - Math Resource', href: 'https://www.zearn.org/', external: true },
            { name: 'Sora - ELA Resource', href: 'https://soraapp.com/welcome', external: true },
            { name: 'Delta Math - Math Resource', href: 'https://www.deltamath.com/', external: true },
            { name: 'Khan Academy - Math Resource', href: 'https://www.khanacademy.org/', external: true },
            { name: 'RAZ-Kids - ELA Resource', href: 'https://www.raz-kids.com/', external: true },
            { name: 'iXL - Math Resource', href: 'https://www.ixl.com/', external: true },
          ]
        },
      ]
    },
    {
      name: 'Resources',
      href: '/resources',
      dropdown: [
        { name: 'Parent\'s Corner', href: '/resources/parents' },
        { name: 'PT Conferences', href: '/conferences' },
        {
          name: 'DOE Resources',
          href: '/resources/doe',
          subdropdown: [
            { name: 'DOE Homepage', href: 'https://www.schools.nyc.gov/', external: true },
            { name: 'Office of Pupil Transportation (OPT)', href: 'https://www.schools.nyc.gov/school-life/transportation/transportation-overview', external: true },
            { name: 'School Food', href: 'https://www.schools.nyc.gov/school-life/food/school-meals', external: true },
            { name: 'NYC Schools Account', href: 'https://www.schools.nyc.gov/learning/student-journey/nyc-schools-account', external: true },
            { name: 'DOE Calendar', href: 'https://www.schools.nyc.gov/calendar', external: true },
          ]
        },
      ]
    },
    {
      name: 'Community',
      href: '/community',
      dropdown: [
        { name: 'TAG PTA', href: '/community/pta' },
      ]
    },
  ];

  const handleDropdownToggle = (index, event) => {
    event.preventDefault();
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled
        ? 'bg-white shadow-lg border-b border-slate-200/50'
        : 'bg-secondary-900'
    }`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:text-primary-900 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:font-medium"
      >
        Skip to main content
      </a>
      <div className="container-padding">
        <div className={`flex justify-between items-center transition-all duration-500 ${
          isScrolled ? 'h-16' : 'h-24'
        }`}>
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-4 group">
            <img
              src="/assets/images/logo.png"
              alt="TAG Scholars Logo"
              className={`w-auto object-contain transition-all duration-500 hover:scale-105 ${
                isScrolled ? 'h-12' : 'h-20'
              }`}
            />
            <div className={`transition-all duration-500 ${isScrolled ? 'hidden lg:block' : 'hidden xl:block'}`}>
              <div className={`font-medium transition-all duration-500 ${
                isScrolled
                  ? 'text-primary-900 text-sm'
                  : 'text-white text-base'
              }`}>
                The Talented and Gifted School
              </div>
              <div className={`font-light transition-all duration-500 ${
                isScrolled
                  ? 'text-primary-700 text-xs'
                  : 'text-white/90 text-sm'
              }`}>
                for Young Scholars
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            <div className="flex items-center space-x-1">
              {navItems.map((item, index) => (
                <div key={index} className="relative group">
                  {item.dropdown ? (
                    <div>
                      <button
                        onMouseEnter={() => setActiveDropdown(index)}
                        aria-expanded={activeDropdown === index}
                        aria-haspopup="true"
                        className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                          isScrolled
                            ? 'text-secondary-700 hover:text-primary-700 hover:bg-primary-50'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {item.name}
                        <ChevronDown aria-hidden="true" className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" />
                      </button>
                      {activeDropdown === index && (
                        <div
                          className="absolute top-full left-0 mt-2 w-64 bg-white backdrop-blur-lg border border-primary-200 shadow-xl rounded-2xl py-2 z-50 animate-slide-up"
                          onMouseLeave={() => {
                            setActiveDropdown(null);
                            setActiveSubdropdown(null);
                          }}
                        >
                          {item.dropdown.map((dropdownItem, dropdownIndex) => (
                            <div key={dropdownIndex} className="relative group/sub">
                              {dropdownItem.subdropdown ? (
                                <div>
                                  <div
                                    className="flex items-center justify-between px-4 py-3 text-sm text-secondary-700 hover:text-primary-700 hover:bg-primary-50 transition-all duration-200 mx-2 rounded-xl cursor-pointer"
                                    onMouseEnter={() => setActiveSubdropdown(dropdownIndex)}
                                  >
                                    <span>{dropdownItem.name}</span>
                                    <ChevronDown className="ml-1 h-4 w-4 -rotate-90" />
                                  </div>
                                  {activeSubdropdown === dropdownIndex && (
                                    <div
                                      className="absolute left-full top-0 ml-1 w-64 bg-white backdrop-blur-lg border border-primary-200 shadow-xl rounded-2xl py-2 z-50"
                                      onMouseEnter={() => setActiveSubdropdown(dropdownIndex)}
                                      onMouseLeave={() => setActiveSubdropdown(null)}
                                    >
                                      {dropdownItem.subdropdown.map((subItem, subIndex) => (
                                        subItem.external ? (
                                          <a
                                            key={subIndex}
                                            href={subItem.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block px-4 py-3 text-sm text-secondary-700 hover:text-primary-700 hover:bg-primary-50 transition-all duration-200 mx-2 rounded-xl"
                                          >
                                            {subItem.name}
                                          </a>
                                        ) : (
                                          <Link
                                            key={subIndex}
                                            to={subItem.href}
                                            className="block px-4 py-3 text-sm text-secondary-700 hover:text-primary-700 hover:bg-primary-50 transition-all duration-200 mx-2 rounded-xl"
                                          >
                                            {subItem.name}
                                          </Link>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <Link
                                  to={dropdownItem.href}
                                  aria-current={location.pathname === dropdownItem.href ? 'page' : undefined}
                                  className="block px-4 py-3 text-sm text-secondary-700 hover:text-primary-700 hover:bg-primary-50 transition-all duration-200 mx-2 rounded-xl"
                                >
                                  {dropdownItem.name}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      aria-current={location.pathname === item.href ? 'page' : undefined}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                        isScrolled
                          ? 'text-slate-700 hover:text-primary-600 hover:bg-white'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="ml-6">
              <Link
                to="/admissions"
                className={`text-sm px-6 py-3 font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                  isScrolled
                    ? 'bg-primary-700 hover:bg-primary-800 text-white'
                    : 'bg-white text-primary-700 hover:bg-primary-50 border border-primary-200 hover:border-primary-400'
                }`}
              >
                Apply Now
              </Link>
            </div>
          </div>

          {/* Language selector mount (visible at every width) */}
          <span id="site-language" className="inline-flex items-center mx-2"></span>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              className={`p-3 rounded-xl transition-all duration-300 ${
                isScrolled
                  ? 'text-slate-700 hover:bg-slate-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              {isMobileMenuOpen ? (
                <X aria-hidden="true" className="h-6 w-6" />
              ) : (
                <Menu aria-hidden="true" className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white backdrop-blur-lg border-t border-slate-200 shadow-xl">
          <div className="container-padding py-6 space-y-2">
            {navItems.map((item, index) => (
              <div key={index}>
                {item.dropdown ? (
                  <div>
                    <button
                      onClick={(e) => handleDropdownToggle(index, e)}
                      aria-expanded={activeDropdown === index}
                      aria-haspopup="true"
                      className="w-full flex items-center justify-between px-4 py-3 text-slate-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
                    >
                      <span className="font-medium">{item.name}</span>
                      <ChevronDown aria-hidden="true" className={`h-5 w-5 transition-transform ${
                        activeDropdown === index ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {activeDropdown === index && (
                      <div className="mt-2 ml-4 space-y-1">
                        {item.dropdown.map((dropdownItem, dropdownIndex) => (
                          <div key={dropdownIndex}>
                            {dropdownItem.subdropdown ? (
                              <div>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setActiveSubdropdown(activeSubdropdown === dropdownIndex ? null : dropdownIndex);
                                  }}
                                  aria-expanded={activeSubdropdown === dropdownIndex}
                                  aria-haspopup="true"
                                  className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                                >
                                  <span>{dropdownItem.name}</span>
                                  <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform ${
                                    activeSubdropdown === dropdownIndex ? 'rotate-180' : ''
                                  }`} />
                                </button>
                                {activeSubdropdown === dropdownIndex && (
                                  <div className="mt-1 ml-4 space-y-1">
                                    {dropdownItem.subdropdown.map((subItem, subIndex) => (
                                      subItem.external ? (
                                        <a
                                          key={subIndex}
                                          href={subItem.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block px-4 py-2 text-xs text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                                          onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                          {subItem.name}
                                        </a>
                                      ) : (
                                        <Link
                                          key={subIndex}
                                          to={subItem.href}
                                          className="block px-4 py-2 text-xs text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                                          onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                          {subItem.name}
                                        </Link>
                                      )
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Link
                                to={dropdownItem.href}
                                aria-current={location.pathname === dropdownItem.href ? 'page' : undefined}
                                className="block px-4 py-2 text-sm text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {dropdownItem.name}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    aria-current={location.pathname === item.href ? 'page' : undefined}
                    className="block px-4 py-3 text-slate-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-4">
              <Link
                to="/admissions"
                className="btn-primary w-full text-center block"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;